<template>
  <v-app id="simple">
    <v-app-bar dark app clipped-right color="primary">
      <v-container fluid class="py-0 fill-height">
        <v-spacer></v-spacer>
        <v-btn class="mr-2 primary" small @click="openPlanDialog" v-if="user"
          >Plan: <b>{{ user?.subscription?.plan?.product }}</b></v-btn
        >
        <v-btn small class="mr-5 orange" to="/plans">{{ $t("upgrade") }}</v-btn>
        <v-menu offset-y>
          <template v-slot:activator="{on, attrs}">
            <v-avatar v-bind="attrs" v-on="on" class="pointer primary lighten-1" size="32">
              <span>{{ user ? user.name.charAt(0).toUpperCase() : "M" }}</span>
            </v-avatar>
          </template>
          <v-list>
            <v-list-item to="/" v-if="!user">
              <v-list-item-title>{{ $t("search") }}</v-list-item-title>
            </v-list-item>
            <v-list-item to="/register" v-if="!user">
              <v-list-item-title>{{ $t("join") }}</v-list-item-title>
            </v-list-item>
            <v-list-item to="/login" v-if="!user">
              <v-list-item-title>{{ $t("login") }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="user">
              <v-list-item-title>{{ user.name || user.username }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openPlanDialog" v-if="user">
              <v-list-item-title>
                Plan: <b>{{ user?.subscription?.plan?.product }}</b>
              </v-list-item-title>
            </v-list-item>

            <v-list-item to="/logout" v-if="user">
              <v-list-item-title>{{ $t("logout") }}</v-list-item-title>
            </v-list-item>
            <v-list-item>
              <ChangeLanguage />
            </v-list-item>
          </v-list>
        </v-menu>
      </v-container>
    </v-app-bar>
    <v-main>
      <router-view></router-view>
      <v-snackbar v-model="data.snackbar.visible" :color="data.snackbar.color">
        {{ data.snackbar.text }}
        <template v-slot:action="{attrs}">
          <v-btn :color="data.snackbar.buttonColor" text v-bind="attrs" @click="data.snackbar.visible = false">{{ $t("close") }}</v-btn>
        </template>
      </v-snackbar>
      <v-dialog v-model="planDialog" max-width="800px">
        <v-card>
          <v-card-title class="headline primary white--text py-4">
            <v-icon left color="white">mdi-card-account-details-outline</v-icon>
            {{ $t("subscription-details") }}
            <v-spacer></v-spacer>
            <v-btn icon dark @click="planDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pt-6">
            <div v-if="user?.subscription?.plan" class="plan-details">
              <!-- Plan Summary Header -->
              <v-row class="mb-4">
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-4 text-center fill-height">
                    <div class="text-overline mb-1">{{ $t("current-plan") }}</div>
                    <div class="text-h5 primary--text font-weight-bold text-uppercase">
                      {{ user.subscription.plan.product }}
                    </div>
                    <v-chip small :color="user.subscription.status === 'active' ? 'success' : 'warning'" class="mt-2 text-uppercase">
                      {{ user.subscription.status }}
                    </v-chip>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-4 text-center fill-height">
                    <div class="text-overline mb-1">{{ user.subscription.type === "onetime" ? $t("expires") : $t("renewal-date") }}</div>
                    <div class="text-h6">
                      {{ user.subscription.end | FormatDateTimeLong }}
                    </div>
                    <div class="text-caption grey--text mt-1" v-if="user.subscription.type">
                      Plan type: <span class="text-uppercase">{{ user.subscription.type }}</span>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-4 text-center fill-height pink lighten-5" v-if="user.subscription.price">
                    <div class="text-overline mb-1">{{ $t("amount") }}</div>
                    <div class="text-h5 pink--text text--darken-2">${{ user.subscription.plan.amount }}</div>
                  </v-card>
                  <v-card outlined class="pa-4  text-center fill-height d-flex align-center justify-center" v-else>
                    <v-btn color="orange" dark depressed block  to="/plans">
                      {{ $t("upgrade-now") }}
                    </v-btn>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Usage and Limits Section -->
              <v-card outlined>
                <v-card-title class="subtitle-1 font-weight-bold">
                  <v-icon left small>mdi-chart-donut</v-icon>
                  {{ $t("usage-and-limits") }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-row>
                    <v-col v-for="(limit, key) in user.subscription.plan.limits" :key="key" cols="12" sm="6" class="py-4">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-capitalize font-weight-medium">
                          {{ $t(key) }}
                        </span>
                        <span class="text-caption">
                          <span :class="{'red--text font-weight-bold': (user.subscription.usage[key] || 0) >= limit}">
                            {{ user.subscription.usage[key] || 0 }}
                          </span>
                          <span class="grey--text"> / {{ limit }}</span>
                        </span>
                      </div>
                      <v-progress-linear :value="((user.subscription.usage[key] || 0) / limit) * 100" :color="(user.subscription.usage[key] || 0) >= limit ? 'red' : (user.subscription.usage[key] || 0) / limit > 0.8 ? 'orange' : 'primary'" height="8" rounded></v-progress-linear>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </div>
            <div v-else class="text-center py-10">
              <v-icon size="64" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
              <p class="mt-4 grey--text text-h6">{{ $t("no-plan-details-available") }}</p>
            </div>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed @click="planDialog = false" min-width="100">
              {{ $t("close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <v-overlay :value="data.showMask" z-index="9999">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-app>
</template>

<script lang="ts">
import {Component, Vue} from "vue-property-decorator"
import EventBus from "./../eventBus"
import ChangeLanguage from "./../libs/i18n/ChangeLanguage.vue"
import {config} from "@/libs/utils"
import {cacheSrv} from "@/libs/cache/cache"
import {subscriptionService} from "./../features/user/subscription-service"

@Component({
  components: {
    ChangeLanguage,
  },
})
export default class SimpleLayout extends Vue {
  subscriptionUpgradeUrl = config.VUE_APP_APP_PAYMENT_URL
  user: any = null
  planDialog = false

  data: any = {
    user: null,
    drawer: null,
    showMask: false,
    snackbar: {
      visible: false,
      text: "",
      buttonColor: "white",
    },
  }

  constructor() {
    super()
    this.user = this.$store.getters.profile
    this.init()
  }
  async init() {
    let userSubscription: any = await subscriptionService.loadSubscription()
    if (userSubscription) {
      Vue.set(this.user, "subscription", userSubscription.subscription)
    }
  }

  async openPlanDialog() {
    await this.refreshSubscriptionDetails()
    this.planDialog = true
  }

  async refreshSubscriptionDetails() {
    let userSubscription: any = await subscriptionService.loadSubscription()
    if (userSubscription) {
      Vue.set(this.user, "subscription", userSubscription.subscription)
    }
  }

  created() {
    EventBus.$on("display-snackbar", this.displaySnackbar)
    EventBus.$on("user-session-event", this.userSessionEvent)
    EventBus.$on("display-mask", this.showMask)
    EventBus.$on("hide-mask", this.hideMask)
  }

  beforeDestroy() {
    EventBus.$off("display-snackbar", this.displaySnackbar)
    EventBus.$off("user-session-event", this.userSessionEvent)
    EventBus.$off("display-mask", this.showMask)
    EventBus.$off("hide-mask", this.hideMask)
  }
  userSessionEvent(payload: any) {
    console.log("userSessionEvent: ", payload)
    Vue.set(this, "user", payload.user)
  }

  gotoUpgrade() {
    window.location.href = this.subscriptionUpgradeUrl
  }

  displaySnackbar(payload: any) {
    this.data.snackbar.visible = false // first close any open snackbar
    this.data.snackbar.text = payload.text
    this.data.snackbar.visible = true

    switch (payload.type) {
      case "success": {
        this.data.snackbar.color = "success"
        this.data.snackbar.buttonColor = "white"
        break
      }
      case "error": {
        this.data.snackbar.color = "error"
        this.data.snackbar.buttonColor = "white"
        break
      }
      case "warning": {
        this.data.snackbar.color = "warning"
        //this.data.snackbar.buttonColor = "white";
        break
      }
      case "info": {
        this.data.snackbar.color = "info"
        //this.data.snackbar.buttonColor = "white";
        break
      }
      default: {
        this.data.snackbar.color = "primary"
        this.data.snackbar.buttonColor = "white"
        break
      }
    }
  }

  showMask(show: boolean) {
    this.data.showMask = show
  }
  hideMask() {
    this.data.showMask = false
  }
}
</script>
